import React, { useState, useRef, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  LayoutDashboard,
  Monitor,
  ChefHat,
  UtensilsCrossed,
  Boxes,
  Package,
  Truck,
  ShoppingBag,
  ArrowLeftRight,
  ClipboardList,
  Users,
  ShoppingCart,
  Wallet,
  CalendarClock,
  Clock,
  BadgePercent,
  MessageSquare,
  BarChart3,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Settings,
  PlusCircle,
  Building2,
  UserCog,
  Shield,
  ShieldCheck,
  Store,
  Check,
  Bell,
  BellRing,
  CheckCircle,
  XCircle,
  ExternalLink,
  MessageCircle,
  CircleDot
} from 'lucide-react';
import { clsx } from 'clsx';
import { ThemeSelector } from './ThemeSelector';
import EngiSpiderIcon from '../assets/EngiSpider_Icon.png';
import notificationSound from '../assets/sound.wav';
import { getPageAccessConfig, getAppPages, getUserRolesFromToken, canAccessPage, getActiveRestaurantId, setActiveRestaurantId, getActiveRole, setActiveRole } from '../utils/roleAccess';
import { organizationService } from '../services/organization';
import { OrganizationRestaurant } from '../types';
import { orderRequestService, OrderRequestNotification, WaiterCallNotification } from '../services/orderRequest';
import { WebSocketProvider } from '../contexts/WebSocketContext';
import { webFcmService } from '../services/fcmService';


type PopupNotif =
  | { type: 'order'; data: OrderRequestNotification }
  | { type: 'waiter'; data: WaiterCallNotification };

interface NavLeaf {
  name: string;
  href: string;
  pageKey: string;
  icon: React.ComponentType<{ className?: string }>;
  highlight?: boolean;
}

interface NavGroup {
  group: true;
  name: string;
  basePath: string;
  icon: React.ComponentType<{ className?: string }>;
  children: NavLeaf[];
}

type NavRow = NavLeaf | NavGroup;
const isGroup = (r: NavRow): r is NavGroup => (r as NavGroup).group === true;

// Icon per route. The nav itself is built from the Role Page Access pages
// (/app-pages) — name, href and order all come from there — so this map only
// decorates known routes; anything else falls back to a neutral icon.
const ICON_BY_HREF: Record<string, React.ComponentType<{ className?: string }>> = {
  '/': LayoutDashboard,
  '/place-order': PlusCircle,
  '/orders': ShoppingCart,
  '/tables': Users,
  '/reservations': CalendarClock,
  '/customers': Users,
  '/promotions': BadgePercent,
  '/items': UtensilsCrossed,
  '/inventory/stock': Package,
  '/inventory/suppliers': Truck,
  '/inventory/purchases': ShoppingBag,
  '/inventory/transfers': ArrowLeftRight,
  '/inventory/recipes': ClipboardList,
  '/inventory/reports': BarChart3,
  '/kitchen': Monitor,
  '/kitchens': ChefHat,
  '/reports': BarChart3,
  '/feedback': MessageSquare,
  '/expenses': Wallet,
  '/whatsapp-logs': MessageCircle,
  '/employee-management': UserCog,
  '/attendance': Clock,
  '/audit-logs': ShieldCheck,
  '/settings': Settings,
  '/profile': User,
  '/organization-onboarding': Building2,
  '/role-page-config': Shield,
};

// Premium soft elevation reused across cards / popovers
const CARD_SHADOW = '0 1px 2px rgba(16,24,40,0.04), 0 12px 30px -16px rgba(16,24,40,0.20)';

// A single leaf nav link (used directly and for non-group items)
const SidebarLeaf: React.FC<{ item: NavLeaf; onNavigate?: () => void }> = ({ item, onNavigate }) => {
  const { currentTheme } = useTheme();
  const c = currentTheme.colors;
  const highlight = item.highlight ?? false;

  return (
    <NavLink to={item.href} end={item.href === '/'} onClick={onNavigate} className="block">
      {({ isActive }) => {
        const filled = highlight || isActive;
        return (
          <div
            className={clsx(
              'group relative flex items-center gap-3 rounded-xl transition-all duration-200',
              highlight ? 'px-3 py-3 mb-1.5' : 'px-3 py-2.5',
              !filled && 'hover:bg-[var(--color-primary-50)] hover:translate-x-0.5'
            )}
            style={{
              background: filled
                ? `linear-gradient(135deg, ${c.primary[500]}, ${c.primary[700]})`
                : 'transparent',
              color: filled ? '#ffffff' : c.text.secondary,
              boxShadow: filled ? `0 12px 26px -12px ${c.primary[500]}` : 'none',
            }}
          >
            <span
              className={clsx('grid place-items-center rounded-lg shrink-0 transition-colors', highlight ? 'h-7 w-7' : 'h-6 w-6')}
              style={{ backgroundColor: filled ? 'rgba(255,255,255,0.20)' : 'transparent' }}
            >
              <item.icon className={highlight ? 'h-[18px] w-[18px]' : 'h-[17px] w-[17px]'} />
            </span>
            <span className={clsx('truncate', highlight ? 'text-[15px] font-bold' : 'text-sm font-medium')}>
              {item.name}
            </span>
          </div>
        );
      }}
    </NavLink>
  );
};

// A collapsible group (e.g. Inventory) whose children share a base path.
const SidebarGroup: React.FC<{ group: NavGroup; onNavigate?: () => void }> = ({ group, onNavigate }) => {
  const { currentTheme } = useTheme();
  const c = currentTheme.colors;
  const location = useLocation();
  const active = location.pathname.startsWith(group.basePath);
  const [open, setOpen] = useState(active);
  useEffect(() => { if (active) setOpen(true); }, [active]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={clsx(
          'w-full flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
          !active && 'hover:bg-[var(--color-primary-50)]'
        )}
        style={{
          background: active ? `linear-gradient(135deg, ${c.primary[500]}, ${c.primary[700]})` : 'transparent',
          color: active ? '#ffffff' : c.text.secondary,
          boxShadow: active ? `0 12px 26px -12px ${c.primary[500]}` : 'none',
        }}
      >
        <span className="flex items-center gap-3">
          <span className="grid place-items-center h-6 w-6 rounded-lg shrink-0"
                style={{ backgroundColor: active ? 'rgba(255,255,255,0.20)' : 'transparent' }}>
            <group.icon className="h-[17px] w-[17px]" />
          </span>
          {group.name}
        </span>
        <ChevronDown className={clsx('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="mt-1.5 ml-[22px] pl-3 border-l space-y-0.5" style={{ borderColor: c.border.primary }}>
          {group.children.map(child => (
            <NavLink
              key={child.href}
              to={child.href}
              onClick={onNavigate}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-200"
              style={({ isActive }) => ({
                backgroundColor: isActive ? c.primary[50] : 'transparent',
                color: isActive ? c.primary[700] : c.text.secondary,
                boxShadow: isActive ? `inset 2px 0 0 ${c.primary[500]}` : 'none',
              })}
            >
              <child.icon className="h-4 w-4 shrink-0" />
              {child.name}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const SidebarNav: React.FC<{ navigation: NavRow[]; onNavigate?: () => void }> = ({ navigation, onNavigate }) => (
  <>
    {navigation.map(row =>
      isGroup(row)
        ? <SidebarGroup key={row.basePath} group={row} onNavigate={onNavigate} />
        : <SidebarLeaf key={row.href} item={row} onNavigate={onNavigate} />
    )}
  </>
);

export const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [restaurantSelectorOpen, setRestaurantSelectorOpen] = useState(false);
  const [roleSelectorOpen, setRoleSelectorOpen] = useState(false);
  const [activeRole, setActiveRoleState] = useState<string | null>(() => getActiveRole());
  const [restaurants, setRestaurants] = useState<OrganizationRestaurant[]>([]);
  const [activeRestaurantId, setActiveRestaurantIdState] = useState<string | null>(
    () => getActiveRestaurantId()
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const prevRequestIdsRef = useRef<Set<string>>(new Set());
  const prevCallIdsRef = useRef<Set<string>>(new Set());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [pendingRequests, setPendingRequests] = useState<OrderRequestNotification[]>([]);
  const [waiterCalls, setWaiterCalls] = useState<WaiterCallNotification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Popup modal queue (one at a time, like mobile)
  const [notifQueue, setNotifQueue] = useState<PopupNotif[]>([]);
  const [currentNotif, setCurrentNotif] = useState<PopupNotif | null>(null);

  // Fetch restaurants for employee users
  useEffect(() => {
    if (user?.organizationId && user?.restaurantRoles?.length) {
      organizationService.getRestaurantsByOrganization(user.organizationId)
        .then(all => {
          const assigned = all.filter(r =>
            user.restaurantRoles!.some(rr => rr.restaurantId === r.id)
          );
          setRestaurants(assigned);
          if (!activeRestaurantId && assigned.length > 0) {
            const firstId = assigned[0].id;
            setActiveRestaurantIdState(firstId);
            setActiveRestaurantId(firstId);
          }
        })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organizationId]);

  // Fetch restaurants for admin users (have organizationCode but no organizationId)
  useEffect(() => {
    if (user?.organizationCode && !user?.organizationId) {
      organizationService.getOrganizationByCode(user.organizationCode)
        .then(org => organizationService.getRestaurantsByOrganization(org.id))
        .then(all => {
          setRestaurants(all);
          if (!activeRestaurantId && all.length > 0) {
            const firstId = all[0].id;
            setActiveRestaurantIdState(firstId);
            setActiveRestaurantId(firstId);
          }
        })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organizationCode]);

  // Auto-select the first role for the active restaurant when none is chosen
  // (or the stored one is no longer valid) — there is no "All" default.
  useEffect(() => {
    if (!user?.restaurantRoles?.length || !activeRestaurantId) return;
    const entry = user.restaurantRoles.find(rr => rr.restaurantId === activeRestaurantId);
    const roles = entry ? (entry.roles as string[]) : [];
    if (roles.length === 0) return;
    if (!activeRole || !roles.includes(activeRole)) {
      setActiveRoleState(roles[0]);
      setActiveRole(roles[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeRestaurantId, activeRole]);

  const handleRestaurantChange = (restaurantId: string) => {
    setActiveRestaurantIdState(restaurantId);
    setActiveRestaurantId(restaurantId);
    // Reset role when restaurant changes
    setActiveRoleState(null);
    setActiveRole(null);
    setRoleSelectorOpen(false);
    setRestaurantSelectorOpen(false);
    setProfileDropdownOpen(false);
    navigate('/');
  };

  const handleRoleChange = (role: string) => {
    setActiveRoleState(role);
    setActiveRole(role);
    setRoleSelectorOpen(false);
  };

  // Derive effective roles: active role > restaurant roles > JWT roles
  let effectiveRoles: string[];
  if (user?.restaurantRoles?.length && activeRestaurantId) {
    const entry = user.restaurantRoles.find(rr => rr.restaurantId === activeRestaurantId);
    const restaurantRoles = entry ? (entry.roles as string[]) : getUserRolesFromToken();
    // If a specific role is selected and it's valid for this restaurant, use only that
    effectiveRoles = (activeRole && restaurantRoles.includes(activeRole))
      ? [activeRole]
      : restaurantRoles;
  } else {
    effectiveRoles = getUserRolesFromToken();
  }

  // Roles available for the active restaurant (for the dropdown)
  const activeRestaurantRoles: string[] = (() => {
    if (!user?.restaurantRoles?.length || !activeRestaurantId) return [];
    const entry = user.restaurantRoles.find(rr => rr.restaurantId === activeRestaurantId);
    return entry ? (entry.roles as string[]) : [];
  })();

  // The nav is built straight from the Role Page Access pages: ordered by their
  // sortOrder, shown only when the active role is granted the page's key, and each
  // links to the page's own href. No hardcoded list — so it can never drift.
  // Pages under /inventory/ are folded into one collapsible "Inventory" group.
  const pageConfig = getPageAccessConfig();
  const leaves: NavLeaf[] = getAppPages()
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .filter(p => canAccessPage(p.key, effectiveRoles, pageConfig))
    .map(p => ({
      name: p.name,
      href: p.href,
      pageKey: p.key,
      icon: ICON_BY_HREF[p.href] ?? CircleDot,
      highlight: p.href === '/place-order',
    }));

  const navigation: NavRow[] = [];
  let inventoryGroup: NavGroup | null = null;
  for (const leaf of leaves) {
    if (leaf.href.startsWith('/inventory/')) {
      if (!inventoryGroup) {
        inventoryGroup = { group: true, name: 'Inventory', basePath: '/inventory', icon: Boxes, children: [] };
        navigation.push(inventoryGroup);
      }
      inventoryGroup.children.push(leaf);
    } else {
      navigation.push(leaf);
    }
  }

  const handleLogout = () => {
    webFcmService.removeToken();
    logout();
    navigate('/login');
    setProfileDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Start looping sound when popup appears, stop when dismissed
  useEffect(() => {
    if (currentNotif) {
      try {
        const audio = new Audio(notificationSound);
        audio.loop = true;
        audio.play().catch(() => {});
        audioRef.current = audio;
      } catch {}
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    }
  }, [currentNotif]);

  const fetchNotifications = useCallback(async () => {
    if (!activeRestaurantId) return;
    try {
      const [requests, calls] = await Promise.all([
        orderRequestService.getPendingRequests(activeRestaurantId),
        orderRequestService.getPendingWaiterCalls(activeRestaurantId),
      ]);
      const newOrders = requests.filter(r => !prevRequestIdsRef.current.has(r.id));
      const newWaiters = calls.filter(c => !prevCallIdsRef.current.has(c.id));
      if (newOrders.length > 0 || newWaiters.length > 0) {
        const incoming: PopupNotif[] = [
          ...newOrders.map(o => ({ type: 'order' as const, data: o })),
          ...newWaiters.map(w => ({ type: 'waiter' as const, data: w })),
        ];
        setNotifQueue(prev => [...prev, ...incoming]);
      }
      prevRequestIdsRef.current = new Set(requests.map(r => r.id));
      prevCallIdsRef.current = new Set(calls.map(c => c.id));
      setPendingRequests(requests);
      setWaiterCalls(calls);
    } catch {}
  }, [activeRestaurantId]);

  // Initial load when restaurantId is available — FCM handles subsequent updates
  useEffect(() => {
    fetchNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRestaurantId]);

  // Initialize FCM — run on login AND on every mount if token is missing
  useEffect(() => {
    if (!user) return;
    webFcmService.init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    // Re-register if token was never stored (e.g. user was already logged in when FCM was set up)
    if (!user) return;
    if (!localStorage.getItem('fcmToken')) {
      webFcmService.init();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FCM foreground message handler
  useEffect(() => {
    if (!user) return;
    let unsubscribe: (() => void) | undefined;
    webFcmService.onForegroundMessage((_title, _body, data) => {
      const type = data?.type;
      if (type === 'ORDER_REQUEST' || type === 'WAITER_CALL') {
        // New notification — fetch and enqueue popup
        fetchNotifications();
      } else if (type === 'ORDER_REQUEST_HANDLED') {
        // Another device accepted/declined — dismiss if this popup is showing
        const handledId = data?.id;
        if (handledId) {
          prevRequestIdsRef.current.add(handledId);
          setCurrentNotif(prev => (prev?.type === 'order' && prev.data.id === handledId ? null : prev));
          setNotifQueue(prev => prev.filter(n => !(n.type === 'order' && n.data.id === handledId)));
          fetchNotifications();
        }
      } else if (type === 'WAITER_CALL_HANDLED') {
        // Another device acknowledged — dismiss if this popup is showing
        const handledId = data?.id;
        if (handledId) {
          prevCallIdsRef.current.add(handledId);
          setCurrentNotif(prev => (prev?.type === 'waiter' && prev.data.id === handledId ? null : prev));
          setNotifQueue(prev => prev.filter(n => !(n.type === 'waiter' && n.data.id === handledId)));
          fetchNotifications();
        }
      } else {
        // Promotional / broadcast notification — show as toast
        toast(_title + (_body ? '\n' + _body : ''), { duration: 6000, icon: '🔔' });
      }
    }).then(unsub => { unsubscribe = unsub; });
    return () => { unsubscribe?.(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(null), 4000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  // Dequeue: show next popup when current is dismissed
  useEffect(() => {
    if (!currentNotif && notifQueue.length > 0) {
      setCurrentNotif(notifQueue[0]);
      setNotifQueue(prev => prev.slice(1));
    }
  }, [currentNotif, notifQueue]);

  const dismissPopup = () => {
    setCurrentNotif(null);
    navigate('/notifications');
  };

  const handlePopupAccept = async () => {
    if (!currentNotif || currentNotif.type !== 'order') return;
    try { await orderRequestService.acceptRequest(currentNotif.data.id); } catch {}
    dismissPopup();
    fetchNotifications();
  };

  const handlePopupDecline = async () => {
    if (!currentNotif || currentNotif.type !== 'order') return;
    try { await orderRequestService.rejectRequest(currentNotif.data.id); } catch {}
    dismissPopup();
    fetchNotifications();
  };

  const handlePopupAcknowledge = async () => {
    if (!currentNotif || currentNotif.type !== 'waiter') return;
    try { await orderRequestService.acknowledgeWaiterCall(currentNotif.data.id); } catch {}
    dismissPopup();
    fetchNotifications();
  };

  const handleAcceptRequest = async (id: string) => {
    try {
      await orderRequestService.acceptRequest(id);
      fetchNotifications();
    } catch {}
  };

  const handleRejectRequest = async (id: string) => {
    try {
      await orderRequestService.rejectRequest(id);
      fetchNotifications();
    } catch {}
  };

  const handleAcknowledgeCall = async (id: string) => {
    try {
      await orderRequestService.acknowledgeWaiterCall(id);
      fetchNotifications();
    } catch {}
  };

  const totalNotifications = pendingRequests.length + waiterCalls.length;

  return (
    <WebSocketProvider restaurantId={activeRestaurantId}>
    <div
      className="min-h-screen"
      style={{
        background: `radial-gradient(1200px circle at 100% -8%, ${currentTheme.colors.primary[500]}1F, transparent 42%), radial-gradient(1000px circle at -8% 2%, ${currentTheme.colors.primary[400]}17, transparent 40%), ${currentTheme.colors.background.primary}`,
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Mobile sidebar */}
      <div className={clsx(
        'fixed inset-0 z-50 lg:hidden',
        sidebarOpen ? 'block' : 'hidden'
      )}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className={clsx(
          "fixed inset-y-0 left-0 flex w-64 flex-col shadow-xl",
          currentTheme.styles.sidebar.border
        )} style={{ background: `linear-gradient(180deg, ${currentTheme.colors.primary[500]}12, transparent 24%), ${currentTheme.colors.background.card}` }}>
          <div className={clsx(
            "flex-shrink-0 flex items-center justify-between h-16 px-4 border-b",
            currentTheme.styles.sidebar.border
          )}>
            <div className="flex items-center gap-3">
              <div className="grid place-items-center h-9 w-9 rounded-xl bg-white"
                   style={{ boxShadow: CARD_SHADOW, border: `1px solid ${currentTheme.colors.border.primary}` }}>
                <img src={EngiSpiderIcon} alt="EngiSpider" className="h-6 w-6" />
              </div>
              <div className="leading-tight">
                <span className="block text-lg font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>Restro360</span>
                <span className="block text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: currentTheme.colors.text.secondary }}>Restaurant POS</span>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 min-h-0 px-3 py-4 space-y-1 overflow-y-auto">
            <SidebarNav navigation={navigation} onNavigate={() => setSidebarOpen(false)} />
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className={clsx(
          "flex flex-1 min-h-0 flex-col border-r",
          currentTheme.styles.sidebar.border
        )} style={{ background: `linear-gradient(180deg, ${currentTheme.colors.primary[500]}12, transparent 24%), ${currentTheme.colors.background.card}` }}>
          <div className={clsx(
            "flex-shrink-0 flex items-center h-16 px-5 border-b",
            currentTheme.styles.sidebar.border
          )}>
            <div className="grid place-items-center h-9 w-9 rounded-xl bg-white"
                 style={{ boxShadow: CARD_SHADOW, border: `1px solid ${currentTheme.colors.border.primary}` }}>
              <img src={EngiSpiderIcon} alt="EngiSpider" className="h-6 w-6" />
            </div>
            <div className="ml-3 leading-tight">
              <span className="block text-lg font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>Restro360</span>
              <span className="block text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: currentTheme.colors.text.secondary }}>Restaurant POS</span>
            </div>
          </div>
          <nav className="flex-1 min-h-0 px-3 py-4 space-y-1 overflow-y-auto">
            <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: currentTheme.colors.text.secondary, opacity: 0.6 }}>Menu</p>
            <SidebarNav navigation={navigation} />
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className={clsx(
          "sticky top-0 z-40 border-b backdrop-blur-xl",
          currentTheme.styles.header.border
        )} style={{
          backgroundColor: `color-mix(in srgb, ${currentTheme.colors.background.card} 75%, transparent)`,
          boxShadow: '0 1px 0 rgba(16,24,40,0.04), 0 10px 30px -22px rgba(16,24,40,0.55)',
        }}>
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden grid place-items-center h-10 w-10 rounded-xl transition-colors hover:bg-[var(--color-primary-50)]"
              style={{ color: currentTheme.colors.text.secondary }}
            >
              <Menu className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-3 flex-1 justify-end">
              <div
                className="hidden sm:flex mr-auto items-center gap-2 rounded-full px-3.5 py-1.5"
                style={{
                  backgroundColor: currentTheme.colors.primary[50],
                  color: currentTheme.colors.primary[700],
                  border: `1px solid ${currentTheme.colors.primary[100]}`,
                }}
              >
                <Store className="h-3.5 w-3.5" />
                <span className="text-sm font-semibold tracking-wide truncate max-w-[200px]">
                  {user?.organizationName || user?.organizationCode}
                </span>
              </div>
              
              {/* Notification Bell */}
              {activeRestaurantId && (
                <div className="relative" ref={notificationRef}>
                  <button
                    onClick={() => setShowNotifications(s => !s)}
                    className="relative grid place-items-center h-10 w-10 rounded-xl transition-colors hover:bg-[var(--color-primary-50)]"
                    title="Notifications"
                  >
                    {totalNotifications > 0 ? (
                      <BellRing className="h-5 w-5 text-red-500" />
                    ) : (
                      <Bell className="h-5 w-5" style={{ color: currentTheme.colors.text.secondary }} />
                    )}
                    {totalNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold leading-none">
                        {totalNotifications > 99 ? '99+' : totalNotifications}
                      </span>
                    )}
                  </button>

                  {showNotifications && (
                    <div className={clsx(
                      'absolute right-0 mt-2 w-96 rounded-lg shadow-xl border z-50 overflow-hidden',
                      currentTheme.styles.card.background,
                      currentTheme.styles.card.border
                    )}>
                      <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: currentTheme.colors.border.primary }}>
                        <h3 className="font-semibold text-sm" style={{ color: currentTheme.colors.text.primary }}>
                          Notifications
                        </h3>
                        {totalNotifications > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">
                            {totalNotifications} pending
                          </span>
                        )}
                      </div>

                      <div className="max-h-[70vh] overflow-y-auto">
                        {/* Waiter Calls */}
                        {waiterCalls.length > 0 && (
                          <div className="p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: currentTheme.colors.text.secondary }}>
                              Waiter Calls
                            </p>
                            {waiterCalls.map(call => (
                              <div key={call.id} className="mb-2 p-3 rounded-lg border border-amber-400" style={{ backgroundColor: '#FEF3C7' }}>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-semibold text-amber-800">🔔 Table: {call.tableName}</p>
                                    <p className="text-xs text-amber-600 mt-0.5">Customer is calling for a waiter</p>
                                  </div>
                                  <button
                                    onClick={() => handleAcknowledgeCall(call.id)}
                                    className="ml-3 flex-shrink-0 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs rounded-lg font-medium transition-colors"
                                  >
                                    OK
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Order Requests */}
                        {pendingRequests.length > 0 && (
                          <div className="p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: currentTheme.colors.text.secondary }}>
                              Order Requests
                            </p>
                            {pendingRequests.map(req => (
                              <div
                                key={req.id}
                                className="mb-3 p-3 rounded-lg border border-blue-400 cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
                                style={{ backgroundColor: '#EFF6FF' }}
                                onClick={() => { navigate('/notifications'); setShowNotifications(false); }}
                              >
                                <div className="mb-2">
                                  <p className="text-sm font-semibold text-blue-800">🪑 {req.tableName || req.tableId}</p>
                                  <p className="text-xs text-blue-600 mt-0.5">{req.customerName} · {req.customerMobile}</p>
                                </div>
                                <div className="space-y-1 mb-2">
                                  {req.items.slice(0, 2).map((item, idx) => (
                                    <div key={idx} className="flex justify-between text-xs text-blue-700">
                                      <span>{item.itemName} × {item.quantity}</span>
                                      <span>₹{(item.unitPrice * item.quantity).toFixed(2)}</span>
                                    </div>
                                  ))}
                                  {req.items.length > 2 && (
                                    <p className="text-xs text-blue-500">+{req.items.length - 2} more items</p>
                                  )}
                                </div>
                                <div className="flex space-x-2" onClick={e => e.stopPropagation()}>
                                  <button
                                    onClick={() => handleAcceptRequest(req.id)}
                                    className="flex-1 flex items-center justify-center space-x-1 px-2 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs rounded-lg font-medium transition-colors"
                                  >
                                    <CheckCircle className="h-3 w-3" />
                                    <span>Accept</span>
                                  </button>
                                  <button
                                    onClick={() => handleRejectRequest(req.id)}
                                    className="flex-1 flex items-center justify-center space-x-1 px-2 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs rounded-lg font-medium transition-colors"
                                  >
                                    <XCircle className="h-3 w-3" />
                                    <span>Reject</span>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Empty state */}
                        {totalNotifications === 0 && (
                          <div className="p-6 text-center">
                            <Bell className="h-8 w-8 mx-auto mb-2" style={{ color: currentTheme.colors.text.secondary }} />
                            <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>No pending notifications</p>
                          </div>
                        )}
                      </div>

                      {/* Footer — link to full page */}
                      <button
                        onClick={() => { navigate('/notifications'); setShowNotifications(false); }}
                        className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-medium border-t transition-colors hover:bg-opacity-10"
                        style={{
                          borderColor: currentTheme.colors.border.primary,
                          color: currentTheme.colors.primary[600],
                        }}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View all notifications &amp; history
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Profile Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center space-x-3 px-2 py-1.5 rounded-xl transition-colors hover:bg-[var(--color-primary-50)]"
                >
                  <div className="text-right text-sm min-w-0">
                    <p className="font-medium truncate" style={{ color: currentTheme.colors.text.primary }}>
                      {user?.name || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                    </p>
                    <p className="text-xs truncate" style={{ color: currentTheme.colors.text.secondary }}>{user?.email}</p>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden ring-2 ring-white"
                      style={{
                        background: `linear-gradient(135deg, ${currentTheme.colors.primary[400]}, ${currentTheme.colors.primary[600]})`,
                        boxShadow: `0 6px 16px -8px ${currentTheme.colors.primary[500]}`,
                      }}
                    >
                      {user?.photo ? (
                        <img src={user.photo} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-4 w-4 text-white" />
                      )}
                    </div>
                    <ChevronDown className={clsx(
                      "h-4 w-4 transition-transform",
                      profileDropdownOpen && "rotate-180"
                    )} style={{ color: currentTheme.colors.text.secondary }} />
                  </div>
                </button>

                {/* Dropdown Menu */}
                {profileDropdownOpen && (
                  <div className={clsx(
                    "absolute right-0 mt-2 w-64 rounded-lg shadow-lg border py-1 z-50",
                    currentTheme.styles.card.background,
                    currentTheme.styles.card.border
                  )}>
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setProfileDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-opacity-10 flex items-center space-x-2"
                      style={{ color: currentTheme.colors.text.primary }}
                    >
                      <User className="h-4 w-4" />
                      <span>Profile</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/settings');
                        setProfileDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-opacity-10 flex items-center space-x-2"
                      style={{ color: currentTheme.colors.text.primary }}
                    >
                      <Settings className="h-4 w-4" />
                      <span>Settings</span>
                    </button>
                    <hr className="my-1" style={{ borderColor: currentTheme.colors.border.primary }} />

                    {/* Restaurant selector (employees only) */}
                    {restaurants.length > 0 && (
                      <div className="px-4 py-2">
                        <button
                          onClick={() => setRestaurantSelectorOpen(!restaurantSelectorOpen)}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors hover:bg-opacity-10"
                          style={{ color: currentTheme.colors.text.primary }}
                        >
                          <div className="flex items-center space-x-2">
                            <Store className="h-4 w-4" style={{ color: currentTheme.colors.text.secondary }} />
                            <span className="font-medium">Restaurant</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs truncate max-w-[80px]" style={{ color: currentTheme.colors.text.secondary }}>
                              {restaurants.find(r => r.id === activeRestaurantId)?.name ?? ''}
                            </span>
                            <ChevronDown
                              className={clsx('h-4 w-4 transition-transform', restaurantSelectorOpen && 'rotate-180')}
                              style={{ color: currentTheme.colors.text.secondary }}
                            />
                          </div>
                        </button>
                        {restaurantSelectorOpen && (
                          <div className="mt-2 space-y-1 pl-6">
                            {restaurants.map(r => (
                              <button
                                key={r.id}
                                onClick={() => handleRestaurantChange(r.id)}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors hover:bg-opacity-10"
                                style={{
                                  backgroundColor: activeRestaurantId === r.id ? currentTheme.colors.primary[100] : 'transparent',
                                  color: activeRestaurantId === r.id ? currentTheme.colors.primary[700] : currentTheme.colors.text.secondary,
                                }}
                              >
                                <div className="flex items-center space-x-3">
                                  <Store className="h-3 w-3" style={{ color: activeRestaurantId === r.id ? currentTheme.colors.primary[600] : currentTheme.colors.text.secondary }} />
                                  <span>{r.name}</span>
                                </div>
                                {activeRestaurantId === r.id && (
                                  <Check className="h-3 w-3" style={{ color: currentTheme.colors.primary[700] }} />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Role selector (shown when restaurant has multiple roles) */}
                    {activeRestaurantRoles.length > 1 && (
                      <div className="px-4 py-2">
                        <button
                          onClick={() => setRoleSelectorOpen(!roleSelectorOpen)}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors hover:bg-opacity-10"
                          style={{ color: currentTheme.colors.text.primary }}
                        >
                          <div className="flex items-center space-x-2">
                            <Shield className="h-4 w-4" style={{ color: currentTheme.colors.text.secondary }} />
                            <span className="font-medium">Role</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs truncate max-w-[80px]" style={{ color: currentTheme.colors.text.secondary }}>
                              {activeRole ?? activeRestaurantRoles[0] ?? '—'}
                            </span>
                            <ChevronDown
                              className={clsx('h-4 w-4 transition-transform', roleSelectorOpen && 'rotate-180')}
                              style={{ color: currentTheme.colors.text.secondary }}
                            />
                          </div>
                        </button>
                        {roleSelectorOpen && (
                          <div className="mt-2 space-y-1 pl-6">
                            {activeRestaurantRoles.map(role => (
                              <button
                                key={role}
                                onClick={() => handleRoleChange(role)}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors hover:bg-opacity-10"
                                style={{
                                  backgroundColor: activeRole === role ? currentTheme.colors.primary[100] : 'transparent',
                                  color: activeRole === role ? currentTheme.colors.primary[700] : currentTheme.colors.text.secondary,
                                }}
                              >
                                <span>{role}</span>
                                {activeRole === role && (
                                  <Check className="h-3 w-3" style={{ color: currentTheme.colors.primary[700] }} />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <ThemeSelector />
                    <hr className="my-1" style={{ borderColor: currentTheme.colors.border.primary }} />
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <Outlet />
        </main>
      </div>

      {/* Toast notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-[100] flex items-center space-x-2 bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg">
          <BellRing className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm font-medium">{toastMessage}</span>
        </div>
      )}

      {/* Notification Popup Modal */}
      {currentNotif && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
          <div
            className="w-full max-w-sm rounded-2xl shadow-2xl p-6"
            style={{ backgroundColor: currentTheme.colors.background.card }}
          >
            {/* Badge */}
            <div
              className="inline-flex items-center px-3 py-1.5 rounded-full mb-4 text-sm font-bold"
              style={{
                backgroundColor: currentNotif.type === 'order' ? currentTheme.colors.primary[100] : '#FEF3C7',
                color: currentNotif.type === 'order' ? currentTheme.colors.primary[700] : '#92400E',
              }}
            >
              {currentNotif.type === 'order' ? '🛒 New Order Request' : '🔔 Waiter Called'}
            </div>

            {currentNotif.type === 'order' ? (
              <>
                <p className="text-lg font-bold mb-1" style={{ color: currentTheme.colors.text.primary }}>
                  {currentNotif.data.tableName || 'Table'} · {currentNotif.data.customerName}
                </p>
                {currentNotif.data.customerMobile && (
                  <p className="text-sm mb-3" style={{ color: currentTheme.colors.text.secondary }}>
                    {currentNotif.data.customerMobile}
                  </p>
                )}
                <hr className="mb-3" style={{ borderColor: currentTheme.colors.border.secondary }} />
                <div className="space-y-2 mb-3">
                  {currentNotif.data.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span style={{ color: currentTheme.colors.text.primary }}>{item.quantity}× {item.itemName}</span>
                      <span style={{ color: currentTheme.colors.text.secondary }}>₹{(item.quantity * item.unitPrice).toFixed(0)}</span>
                    </div>
                  ))}
                </div>
                {currentNotif.data.items.length > 0 && (
                  <div className="flex justify-between text-sm font-semibold mb-4">
                    <span style={{ color: currentTheme.colors.text.secondary }}>Total</span>
                    <span style={{ color: currentTheme.colors.text.primary }}>
                      ₹{currentNotif.data.items.reduce((s, i) => s + i.quantity * i.unitPrice, 0).toFixed(0)}
                    </span>
                  </div>
                )}
                <hr className="mb-4" style={{ borderColor: currentTheme.colors.border.secondary }} />
                <div className="flex gap-3">
                  <button
                    onClick={handlePopupDecline}
                    className="flex-1 py-3 rounded-xl text-sm font-bold transition-colors"
                    style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}
                  >
                    Decline
                  </button>
                  <button
                    onClick={handlePopupAccept}
                    className="flex-1 py-3 rounded-xl text-sm font-bold text-white transition-colors"
                    style={{ backgroundColor: currentTheme.colors.primary[600] }}
                  >
                    Accept
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-lg font-bold mb-1" style={{ color: currentTheme.colors.text.primary }}>
                  {currentNotif.data.tableName}
                </p>
                <p className="text-sm mb-5" style={{ color: currentTheme.colors.text.secondary }}>
                  This table is requesting assistance
                </p>
                <button
                  onClick={handlePopupAcknowledge}
                  className="w-full py-3 rounded-xl text-sm font-bold text-white transition-colors"
                  style={{ backgroundColor: currentTheme.colors.primary[600] }}
                >
                  Acknowledge
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
    </WebSocketProvider>
  );
};